/**
 * Boring Agent Web UI
 *
 * Premium responsive chat interface powered by Tailwind CSS v4 + Framer Motion.
 * Native platform capabilities: PWA, swipe gestures, haptic feedback, voice input,
 * native share, drag-and-drop, offline detection, safe areas, reduced motion.
 */

import { AnimatePresence, motion } from "framer-motion";
import {
	Bot,
	Check,
	ChevronDown,
	ChevronLeft,
	Command,
	Copy,
	Download,
	GitBranch,
	GitCommit,
	ImagePlus,
	Menu,
	Mic,
	MicOff,
	Paperclip,
	Plus,
	RefreshCw,
	RotateCcw,
	Save,
	Send,
	Settings,
	Share2,
	Square,
	Terminal,
	Trash2,
	User,
	X,
	Zap,
	ZapOff,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CommandPalette } from "./components/CommandPalette";
import { ConnectionStatusBar } from "./components/ConnectionStatus";
import { ConversationList } from "./components/ConversationList";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FileEditorPanel } from "./components/FileEditorPanel";
import { FileTree } from "./components/FileTree";
import { LoadingScreen } from "./components/LoadingScreen";
import { MarkdownRenderer } from "./components/MarkdownRenderer";
import { SettingsPanel } from "./components/SettingsPanel";
import { SkeletonList } from "./components/SkeletonMessage";
import { ToastProvider, useToast } from "./components/Toast";
import { TypingIndicator } from "./components/TypingIndicator";
import { WelcomeScreen } from "./components/WelcomeScreen";
import { WorkspaceToolbar } from "./components/WorkspaceToolbar";
import { useAgentChat } from "./hooks/useAgentChat";
import { useConversations } from "./hooks/useConversations";
import { useDocumentTitle } from "./hooks/useDocumentTitle";
import { useHaptic } from "./hooks/useHaptic";
import { useHeaderScroll } from "./hooks/useHeaderScroll";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useIsDesktop, useIsMobile, useIsTablet, useReducedMotion } from "./hooks/useMediaQuery";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { usePageVisibility } from "./hooks/usePageVisibility";
import { usePullToRefresh } from "./hooks/usePullToRefresh";
import { useRelativeTime } from "./hooks/useRelativeTime";
import { useScrollToBottom } from "./hooks/useScrollToBottom";
import { useSound } from "./hooks/useSound";
import { useSwipe } from "./hooks/useSwipe";
import { useTheme } from "./hooks/useTheme";
import { useVoiceInput } from "./hooks/useVoiceInput";
import { useWorkspace } from "./hooks/useWorkspace";
import { createNewConversation } from "./lib/agent-api";
import { cn } from "./lib/utils";

/* ------------------------------------------------------------------ */
/*  Theme color sync                                                  */
/* ------------------------------------------------------------------ */
function useThemeColorSync() {
	const { resolvedTheme } = useTheme();
	useEffect(() => {
		const meta = document.getElementById("theme-color") as HTMLMetaElement | null;
		if (meta) {
			meta.content = resolvedTheme === "dark" ? "#0f172a" : "#ffffff";
		}
		document.documentElement.style.colorScheme = resolvedTheme;
	}, [resolvedTheme]);
}

/* ------------------------------------------------------------------ */
/*  Connection Badge (header)                                         */
/* ------------------------------------------------------------------ */
function ConnectionBadge({ connected }: { connected: boolean }) {
	return (
		<div
			className={cn(
				"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium",
				connected ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
			)}
		>
			{connected ? (
				<>
					<Zap className="w-3 h-3" />
					<span className="hidden sm:inline">Connected</span>
				</>
			) : (
				<>
					<ZapOff className="w-3 h-3" />
					<span className="hidden sm:inline">Reconnecting…</span>
				</>
			)}
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  Message Bubble                                                    */
/* ------------------------------------------------------------------ */
function MessageBubble({
	message,
	isStreaming,
	isGrouped,
	onRetry,
}: {
	message: {
		id: string;
		role: "user" | "assistant";
		content: string;
		timestamp?: number;
		parts?: Array<{ type: string; text?: string; image?: string }>;
		reasoning?: string;
		toolCalls?: Array<{
			id: string;
			name: string;
			status: "running" | "done" | "error";
			inputPreview?: string;
			errorText?: string;
		}>;
	};
	isStreaming?: boolean;
	isGrouped?: boolean;
	onRetry?: () => void;
}) {
	const [reasoningOpen, setReasoningOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const { toast } = useToast();
	const { light: hapticLight } = useHaptic();
	const timeText = useRelativeTime(message.timestamp ?? Date.now());
	const isUser = message.role === "user";

	const copy = async () => {
		await navigator.clipboard.writeText(message.content);
		setCopied(true);
		toast("Copied to clipboard", "success");
		hapticLight();
		setTimeout(() => setCopied(false), 2000);
	};

	const share = async () => {
		if (typeof navigator !== "undefined" && "share" in navigator) {
			try {
				await navigator.share({ text: message.content });
			} catch {
				// cancelled
			}
		} else {
			copy();
		}
	};

	const hasShare = typeof navigator !== "undefined" && "share" in navigator;

	return (
		<motion.div
			initial={{ opacity: 0, y: 12, scale: 0.98 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			transition={{ duration: 0.2, ease: "easeOut" }}
			className={cn(
				"flex gap-3 px-4 sm:px-6",
				isUser ? "flex-row-reverse" : "flex-row",
				isGrouped ? "py-1" : "py-3"
			)}
		>
			{/* Avatar — hidden when grouped */}
			<div
				className={cn(
					"shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-opacity",
					isUser ? "bg-primary text-primary-foreground" : "bg-card border border-border",
					isGrouped && "opacity-0"
				)}
			>
				{isUser ? (
					<User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
				) : (
					<Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
				)}
			</div>

			{/* Content */}
			<div
				className={cn(
					"flex-1 min-w-0",
					isUser ? "max-w-[85%] sm:max-w-[75%]" : "max-w-[90%] sm:max-w-[85%]"
				)}
			>
				<div
					className={cn(
						"relative group px-3.5 py-2.5 sm:px-4 sm:py-3 text-sm leading-relaxed",
						isUser
							? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
							: "bg-card border border-border rounded-2xl rounded-tl-sm"
					)}
				>
					{/* Tool-call chips (assistant only) */}
					{!isUser && message.toolCalls && message.toolCalls.length > 0 && (
						<div className="flex flex-wrap gap-1.5 mb-2">
							{message.toolCalls.map((tc) => (
								<span
									key={tc.id}
									className={cn(
										"inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-mono",
										tc.status === "running" &&
											"bg-blue-500/10 text-blue-300 border border-blue-500/30",
										tc.status === "done" &&
											"bg-green-500/10 text-green-300 border border-green-500/30",
										tc.status === "error" && "bg-red-500/10 text-red-300 border border-red-500/30"
									)}
									title={tc.errorText ?? tc.inputPreview ?? ""}
								>
									{tc.status === "running" && (
										<span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
									)}
									{tc.status === "done" && <Check className="w-3 h-3" />}
									{tc.status === "error" && "⚠"} {tc.name}
								</span>
							))}
						</div>
					)}

					{/* Reasoning (collapsible) */}
					{!isUser && message.reasoning && (
						<details
							className="mb-2 text-xs text-muted-foreground"
							open={reasoningOpen}
							onToggle={(e) => setReasoningOpen((e.target as HTMLDetailsElement).open)}
						>
							<summary className="cursor-pointer select-none hover:text-foreground transition-colors">
								💭 {isStreaming && !message.content ? "Thinking…" : "Reasoning"} (
								{message.reasoning.length} chars)
							</summary>
							<div className="mt-1.5 pl-3 border-l-2 border-border whitespace-pre-wrap font-mono">
								{message.reasoning}
							</div>
						</details>
					)}

					{message.content ? (
						isUser ? (
							<p className="whitespace-pre-wrap">{message.content}</p>
						) : (
							<MarkdownRenderer content={message.content} />
						)
					) : isStreaming &&
						!message.reasoning &&
						(!message.toolCalls || message.toolCalls.length === 0) ? (
						<TypingIndicator />
					) : null}

					{/* Inline actions for assistant messages */}
					{!isUser && message.content && (
						<div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 p-1 rounded-lg bg-card border border-border shadow-sm">
							<button
								type="button"
								onClick={copy}
								className="p-1.5 rounded-md hover:bg-secondary transition-colors"
								title="Copy"
							>
								{copied ? (
									<Check className="w-3 h-3 text-green-400" />
								) : (
									<Copy className="w-3 h-3 text-muted-foreground" />
								)}
							</button>
							{hasShare && (
								<button
									type="button"
									onClick={share}
									className="p-1.5 rounded-md hover:bg-secondary transition-colors"
									title="Share"
								>
									<Share2 className="w-3 h-3 text-muted-foreground" />
								</button>
							)}
							{onRetry && (
								<button
									type="button"
									onClick={onRetry}
									className="p-1.5 rounded-md hover:bg-secondary transition-colors"
									title="Retry"
								>
									<RotateCcw className="w-3 h-3 text-muted-foreground" />
								</button>
							)}
						</div>
					)}
					{/* Timestamp + status */}
					{!isGrouped && (
						<div
							className={cn(
								"text-[10px] text-muted-foreground mt-1 px-1 flex items-center gap-1",
								isUser ? "justify-end" : "justify-start"
							)}
						>
							{isUser && <Check className="w-2.5 h-2.5 text-primary-foreground/60" />}
							<span>{timeText}</span>
						</div>
					)}
				</div>
			</div>
		</motion.div>
	);
}

/* ------------------------------------------------------------------ */
/*  Sidebar                                                           */
/* ------------------------------------------------------------------ */
function Sidebar({
	open,
	onClose,
	onNewChat,
	width,
	onWidthChange,
	workspace,
	onFileSelect,
	selectedFilePath,
	conversations,
	onSelectConversation,
}: {
	open: boolean;
	onClose: () => void;
	onNewChat: () => void;
	width: number;
	onWidthChange: (w: number) => void;
	workspace: ReturnType<typeof useWorkspace>;
	onFileSelect: (path: string) => void;
	selectedFilePath?: string;
	conversations: ReturnType<typeof useConversations>;
	onSelectConversation: (id: string) => void;
}) {
	const isDesktop = useIsDesktop();
	const resizeRef = useRef<HTMLDivElement>(null);
	const isResizing = useRef(false);
	const startX = useRef(0);
	const startWidth = useRef(width);
	const [newFileName, setNewFileName] = useState("");
	const [showNewFile, setShowNewFile] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	const [showNewFolder, setShowNewFolder] = useState(false);

	const startResize = useCallback(
		(e: React.MouseEvent) => {
			isResizing.current = true;
			startX.current = e.clientX;
			startWidth.current = width;
			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
		},
		[width]
	);

	useEffect(() => {
		const onMove = (e: MouseEvent) => {
			if (!isResizing.current) return;
			const delta = e.clientX - startX.current;
			const newWidth = Math.min(Math.max(startWidth.current + delta, 200), 400);
			onWidthChange(newWidth);
		};
		const onUp = () => {
			isResizing.current = false;
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
		document.addEventListener("mousemove", onMove);
		document.addEventListener("mouseup", onUp);
		return () => {
			document.removeEventListener("mousemove", onMove);
			document.removeEventListener("mouseup", onUp);
		};
	}, [onWidthChange]);

	const gitEnabled = workspace.gitInfo && typeof workspace.gitInfo === "object";

	return (
		<>
			{/* Mobile overlay */}
			{open && !isDesktop && (
				<motion.button
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					type="button"
					className="fixed inset-0 bg-black/50 z-40 lg:hidden"
					onClick={onClose}
					aria-label="Close sidebar"
				/>
			)}

			{/* Sidebar */}
			<motion.aside
				initial={false}
				animate={{
					x: open || isDesktop ? 0 : -width,
					opacity: open || isDesktop ? 1 : 0,
				}}
				transition={{ type: "spring", damping: 30, stiffness: 250 }}
				className={cn(
					"fixed lg:static inset-y-0 left-0 z-50 bg-card border-r border-border flex flex-col overflow-hidden",
					"lg:translate-x-0 lg:opacity-100"
				)}
				style={{ width }}
			>
				<div className="flex items-center justify-between p-4 border-b border-border">
					<div className="flex items-center gap-2 min-w-0">
						<Bot className="w-5 h-5 text-primary shrink-0" />
						<span className="font-semibold truncate">Boring Agent</span>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-colors shrink-0"
					>
						<X className="w-4 h-4" />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto">
					{/* New Chat */}
					<div className="p-3">
						<button
							type="button"
							onClick={() => {
								onNewChat();
								onClose();
							}}
							className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-medium"
						>
							<Plus className="w-4 h-4 shrink-0" />
							<span className="truncate">New Chat</span>
						</button>
					</div>

					{/* Conversations */}
					<div className="border-t border-border">
						<ConversationList
							conversations={conversations.conversations}
							activeId={conversations.activeId}
							loading={conversations.loading}
							onSelect={(id) => {
								onSelectConversation(id);
								onClose();
							}}
							onNew={() => {
								onNewChat();
								onClose();
							}}
							onDelete={(id) => conversations.remove(id)}
						/>
					</div>

					{/* Workspace Section */}
					<div className="border-t border-border">
						<div className="px-3 pt-3 pb-1">
							<p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
								Workspace
							</p>
						</div>
						<WorkspaceToolbar
							onNewFile={() => setShowNewFile(true)}
							onNewFolder={() => setShowNewFolder(true)}
							onGitInit={workspace.initGit}
							onGitCommit={() => {
								const msg = window.prompt("Commit message:");
								if (msg) workspace.gitCommit(msg);
							}}
							onRefresh={workspace.refresh}
							gitEnabled={!!gitEnabled}
						/>
						{showNewFile && (
							<div className="px-3 pb-2 flex items-center gap-1">
								<input
									type="text"
									value={newFileName}
									onChange={(e) => setNewFileName(e.target.value)}
									placeholder="filename.ts"
									className="flex-1 px-2 py-1 text-[10px] rounded-md bg-secondary/50 border border-border outline-none"
									onKeyDown={(e) => {
										if (e.key === "Enter" && newFileName.trim()) {
											workspace.createFile(`/${newFileName.trim()}`);
											setNewFileName("");
											setShowNewFile(false);
										}
										if (e.key === "Escape") {
											setShowNewFile(false);
										}
									}}
								/>
								<button
									type="button"
									onClick={() => {
										if (newFileName.trim()) {
											workspace.createFile(`/${newFileName.trim()}`);
											setNewFileName("");
										}
										setShowNewFile(false);
									}}
									className="p-1 rounded-md bg-primary text-primary-foreground"
								>
									<Plus className="w-3 h-3" />
								</button>
								<button
									type="button"
									onClick={() => setShowNewFile(false)}
									className="p-1 rounded-md hover:bg-secondary"
								>
									<X className="w-3 h-3" />
								</button>
							</div>
						)}
						{showNewFolder && (
							<div className="px-3 pb-2 flex items-center gap-1">
								<input
									type="text"
									value={newFolderName}
									onChange={(e) => setNewFolderName(e.target.value)}
									placeholder="folder-name"
									className="flex-1 px-2 py-1 text-[10px] rounded-md bg-secondary/50 border border-border outline-none"
									onKeyDown={(e) => {
										if (e.key === "Enter" && newFolderName.trim()) {
											workspace.createDir(`/${newFolderName.trim()}`);
											setNewFolderName("");
											setShowNewFolder(false);
										}
										if (e.key === "Escape") {
											setShowNewFolder(false);
										}
									}}
								/>
								<button
									type="button"
									onClick={() => {
										if (newFolderName.trim()) {
											workspace.createDir(`/${newFolderName.trim()}`);
											setNewFolderName("");
										}
										setShowNewFolder(false);
									}}
									className="p-1 rounded-md bg-primary text-primary-foreground"
								>
									<Plus className="w-3 h-3" />
								</button>
								<button
									type="button"
									onClick={() => setShowNewFolder(false)}
									className="p-1 rounded-md hover:bg-secondary"
								>
									<X className="w-3 h-3" />
								</button>
							</div>
						)}
						<div className="px-1 pb-3">
							<FileTree
								entries={workspace.tree}
								selectedPath={selectedFilePath}
								onSelect={(path, entry) => {
									if (entry.type === "file") {
										onFileSelect(path);
									}
								}}
								onDelete={(path) => {
									if (window.confirm(`Delete ${path}?`)) {
										workspace.removeFile(path);
									}
								}}
							/>
						</div>
					</div>
				</div>

				<div className="p-3 border-t border-border text-xs text-muted-foreground">
					<p className="truncate">v1.0.0 — Cloudflare Edge</p>
				</div>

				{/* Desktop resize handle */}
				{isDesktop && (
					<div
						ref={resizeRef}
						onMouseDown={startResize}
						className="absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 transition-colors z-10"
					/>
				)}
			</motion.aside>
		</>
	);
}

/* ------------------------------------------------------------------ */
/*  App Content                                                       */
/* ------------------------------------------------------------------ */
function AppContent() {
	const {
		messages,
		setMessages,
		input,
		setInput,
		isConnected,
		isStreaming,
		isThinking,
		attachments,
		addAttachment,
		removeAttachment,
		sendMessage,
		stop,
		clearChat,
	} = useAgentChat();
	const { theme, setTheme } = useTheme();
	const { enabled: soundEnabled, setEnabled: setSoundEnabled, playSend, playReceive } = useSound();
	const [fontSize, setFontSize] = useState(() => {
		if (typeof localStorage === "undefined") return 16;
		return Number(localStorage.getItem("boring-agent-font-size")) || 16;
	});
	const { toast } = useToast();
	const { light: hapticLight, medium: hapticMedium } = useHaptic();
	const isMobile = useIsMobile();
	const isTablet = useIsTablet();
	const isDesktop = useIsDesktop();
	const reducedMotion = useReducedMotion();
	const isOnline = useOnlineStatus();
	const isVisible = usePageVisibility();

	const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
	const [sidebarWidth, setSidebarWidth] = useState(() => {
		if (typeof localStorage === "undefined") return 288;
		return Number(localStorage.getItem("boring-agent-sidebar-width")) || 288;
	});
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [paletteOpen, setPaletteOpen] = useState(false);
	const [dragOver, setDragOver] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [editorOpen, setEditorOpen] = useState(false);
	const [editorPath, setEditorPath] = useState("");
	const workspace = useWorkspace();
	const conversations = useConversations();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const mainRef = useRef<HTMLElement>(null);
	const { showButton: showScrollButton, scrollToBottom } = useScrollToBottom(mainRef);
	const headerHidden = useHeaderScroll(mainRef);
	const {
		containerRef: pullRef,
		pulling,
		progress,
	} = usePullToRefresh({
		enabled: isMobile,
		onRefresh: () => window.location.reload(),
	});

	useThemeColorSync();
	useDocumentTitle(messages);

	/* Conversation management */
	const handleNewChat = useCallback(async () => {
		// Auto-save current conversation if it has messages
		if (messages.length > 0) {
			const firstUserMsg = messages.find((m) => m.role === "user");
			const title = firstUserMsg
				? firstUserMsg.content.slice(0, 40) + (firstUserMsg.content.length > 40 ? "…" : "")
				: "Untitled";
			const id = conversations.activeId || crypto.randomUUID();
			try {
				await conversations.save(id, title);
			} catch {
				// Ignore save errors
			}
		}
		try {
			await createNewConversation();
		} catch {
			// Fallback: just clear locally
		}
		conversations.activate(null);
		clearChat();
		toast("New chat started", "info");
	}, [messages, conversations, clearChat, toast]);

	const handleSelectConversation = useCallback(
		async (id: string) => {
			// Auto-save current conversation before switching
			if (messages.length > 0 && conversations.activeId) {
				const firstUserMsg = messages.find((m) => m.role === "user");
				const title = firstUserMsg
					? firstUserMsg.content.slice(0, 40) + (firstUserMsg.content.length > 40 ? "…" : "")
					: "Untitled";
				try {
					await conversations.save(conversations.activeId, title);
				} catch {
					// Ignore save errors
				}
			}
			try {
				const result = await conversations.load(id);
				if (result?.messages) {
					setMessages(result.messages);
				} else {
					clearChat();
				}
			} catch (e) {
				toast("Failed to load conversation", "error");
			}
		},
		[messages, conversations, setMessages, clearChat, toast]
	);

	/* Persist settings */
	useEffect(() => {
		localStorage.setItem("boring-agent-sidebar-width", String(sidebarWidth));
	}, [sidebarWidth]);
	useEffect(() => {
		localStorage.setItem("boring-agent-font-size", String(fontSize));
	}, [fontSize]);

	/* Auto-focus input on load */
	useEffect(() => {
		const timer = setTimeout(() => textareaRef.current?.focus(), 300);
		return () => clearTimeout(timer);
	}, []);

	/* Auto-open sidebar on desktop, close on mobile */
	useEffect(() => {
		setSidebarOpen(isDesktop);
	}, [isDesktop]);

	/* Swipe: right edge → open sidebar, left swipe on main → close sidebar */
	useSwipe({
		enabled: isMobile,
		onSwipeRight: () => {
			if (!sidebarOpen) {
				setSidebarOpen(true);
				hapticLight();
			}
		},
		onSwipeLeft: () => {
			if (sidebarOpen) {
				setSidebarOpen(false);
				hapticLight();
			}
		},
	});

	/* Voice input */
	const handleVoiceTranscript = useCallback(
		(text: string) => {
			setInput((prev) => prev + (prev ? " " : "") + text);
			textareaRef.current?.focus();
		},
		[setInput]
	);
	const {
		isListening,
		isSupported: voiceSupported,
		toggle: toggleVoice,
	} = useVoiceInput(handleVoiceTranscript);

	/* Auto-scroll */
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally depends on messages and streaming state
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth" });
	}, [messages, isStreaming, reducedMotion]);

	/* Auto-resize textarea */
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally depends on input content
	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
	}, [input]);

	/* Keyboard shortcuts */
	useKeyboardShortcuts({
		onNewChat: () => {
			handleNewChat();
			hapticMedium();
		},
		onFocusInput: () => {
			textareaRef.current?.focus();
			setPaletteOpen(false);
		},
		onToggleSidebar: () => setSidebarOpen((s) => !s),
		onSendMessage: () => handleSend(),
	});

	/* Beforeunload: warn about unsent messages */
	useEffect(() => {
		const handler = (e: BeforeUnloadEvent) => {
			if (input.trim() || isStreaming) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [input, isStreaming]);

	/* Drag & drop file upload */
	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setDragOver(true);
	}, []);
	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setDragOver(false);
	}, []);
	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer.files?.[0];
		if (file) processFile(file);
	}, []);

	const processFile = async (file: File) => {
		hapticLight();
		const isText =
			file.type.startsWith("text/") ||
			/\.(txt|md|json|js|ts|tsx|jsx|css|html|yaml|yml|xml|csv|sh|py|rb|go|rs|c|cpp|h|java|kt|swift|php)$/i.test(
				file.name
			);

		if (isText && file.size < 1024 * 1024) {
			try {
				const text = await file.text();
				const path = `/uploads/${file.name}`;
				await workspace.saveFile(path, text);
				toast(`Uploaded "${file.name}" to workspace`, "success");
				setInput((prev) => prev + (prev ? "\n" : "") + `I uploaded ${file.name} to ${path}.`);
				textareaRef.current?.focus();
			} catch (e) {
				toast(`Upload failed: ${e instanceof Error ? e.message : String(e)}`, "error");
			}
		} else if (file.type.startsWith("image/") && file.size < 5 * 1024 * 1024) {
			try {
				const reader = new FileReader();
				reader.onload = () => {
					const src = reader.result as string;
					addAttachment(file, src);
					toast(`Image "${file.name}" attached`, "success");
				};
				reader.readAsDataURL(file);
			} catch (e) {
				toast(`Image attach failed: ${e instanceof Error ? e.message : String(e)}`, "error");
			}
		} else {
			toast(
				`File "${file.name}" attached (${(file.size / 1024).toFixed(1)} KB) — text files are saved to workspace, images up to 5MB are attached`,
				"info"
			);
		}
	};

	const handleSend = () => {
		const text = input.trim();
		if (!text || isStreaming || !isConnected) return;
		sendMessage();
		hapticLight();
		playSend();
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleSuggestion = (text: string) => {
		setIsLoading(true);
		sendMessage(text);
		setTimeout(() => setIsLoading(false), 500);
	};

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		processFile(file);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleRetry = useCallback(() => {
		// Retry last assistant message by removing it and re-sending user message
		const lastUserMsg = [...messages].reverse().find((m) => m.role === "user");
		if (lastUserMsg) {
			clearChat();
			setTimeout(() => sendMessage(lastUserMsg.content), 100);
		}
	}, [messages, clearChat, sendMessage]);

	const hasMessages = messages.length > 0;

	/* Group consecutive messages */
	const groupedMessages = messages.reduce<
		Array<{ message: (typeof messages)[0]; isGrouped: boolean }>
	>((acc, msg, i) => {
		const prev = messages[i - 1];
		const isGrouped = prev?.role === msg.role;
		acc.push({ message: msg, isGrouped });
		return acc;
	}, []);

	const paletteCommands = [
		{
			id: "new-chat",
			label: "New Chat",
			icon: <Plus className="w-4 h-4" />,
			shortcut: "⌘ N",
			action: () => {
				handleNewChat();
				hapticMedium();
			},
		},
		{
			id: "clear-chat",
			label: "Clear Conversation",
			icon: <Trash2 className="w-4 h-4" />,
			action: () => {
				handleNewChat();
				toast("Conversation cleared", "info");
			},
		},
		{
			id: "settings",
			label: "Open Settings",
			icon: <Settings className="w-4 h-4" />,
			shortcut: "⌘ ,",
			action: () => setSettingsOpen(true),
		},
		{
			id: "toggle-sidebar",
			label: "Toggle Sidebar",
			icon: <Menu className="w-4 h-4" />,
			shortcut: "⌘ B",
			action: () => setSidebarOpen((s) => !s),
		},
		{
			id: "focus-input",
			label: "Focus Input",
			icon: <ChevronLeft className="w-4 h-4" />,
			shortcut: "⌘ K",
			action: () => textareaRef.current?.focus(),
		},
		{
			id: "export",
			label: "Export Conversation",
			icon: <Download className="w-4 h-4" />,
			action: () => {
				const text = messages.map((m) => `**${m.role}**: ${m.content}`).join("\n\n");
				const blob = new Blob([text], { type: "text/markdown" });
				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = `boring-agent-chat-${new Date().toISOString().slice(0, 10)}.md`;
				a.click();
				URL.revokeObjectURL(url);
				toast("Conversation exported", "success");
			},
		},
		{
			id: "save-conversation",
			label: "Save Conversation",
			icon: <Save className="w-4 h-4" />,
			action: () => {
				if (messages.length === 0) {
					toast("Nothing to save", "info");
					return;
				}
				const firstUserMsg = messages.find((m) => m.role === "user");
				const title = firstUserMsg
					? firstUserMsg.content.slice(0, 40) + (firstUserMsg.content.length > 40 ? "…" : "")
					: "Untitled";
				const id = conversations.activeId || crypto.randomUUID();
				conversations
					.save(id, title)
					.then(() => toast("Conversation saved", "success"))
					.catch((e) =>
						toast(`Save failed: ${e instanceof Error ? e.message : String(e)}`, "error")
					);
			},
		},
		{
			id: "refresh-workspace",
			label: "Refresh Workspace",
			icon: <RefreshCw className="w-4 h-4" />,
			action: () => {
				workspace.refresh();
				toast("Workspace refreshed", "info");
			},
		},
		{
			id: "git-status",
			label: "Git Status",
			icon: <GitBranch className="w-4 h-4" />,
			action: async () => {
				try {
					const { gitStatus } = await import("./lib/agent-api");
					const status = await gitStatus();
					toast(`Git status: ${JSON.stringify(status).slice(0, 100)}`, "info");
				} catch (e) {
					toast(`Git status failed: ${e instanceof Error ? e.message : String(e)}`, "error");
				}
			},
		},
		{
			id: "git-init",
			label: "Git Init",
			icon: <GitCommit className="w-4 h-4" />,
			action: () => {
				workspace
					.initGit()
					.then(() => toast("Git repository initialized", "success"))
					.catch((e) =>
						toast(`Git init failed: ${e instanceof Error ? e.message : String(e)}`, "error")
					);
			},
		},
		{
			id: "run-code",
			label: "Run Code Snippet",
			icon: <Terminal className="w-4 h-4" />,
			action: () => {
				const code = window.prompt("Enter JavaScript code to run:");
				if (!code) return;
				import("./lib/agent-api")
					.then(({ runCode }) => runCode(code))
					.then((result) => toast(`Result: ${JSON.stringify(result).slice(0, 200)}`, "success"))
					.catch((e) =>
						toast(`Execution failed: ${e instanceof Error ? e.message : String(e)}`, "error")
					);
			},
		},
		{
			id: "toggle-theme",
			label: "Toggle Theme",
			icon: <Zap className="w-4 h-4" />,
			action: () => setTheme(theme === "dark" ? "light" : "dark"),
		},
	];

	/* Tablet: sidebar always visible but collapsible */
	const showSidebar = isDesktop || (isTablet && sidebarOpen) || (isMobile && sidebarOpen);

	/* Play sound + notify when assistant finishes responding */
	const prevStreaming = useRef(isStreaming);
	useEffect(() => {
		if (
			prevStreaming.current &&
			!isStreaming &&
			messages.length > 0 &&
			messages[messages.length - 1].role === "assistant"
		) {
			playReceive();
			if (!isVisible && "Notification" in window && Notification.permission === "granted") {
				new Notification("Boring Agent", {
					body: messages[messages.length - 1].content.slice(0, 100) || "Response ready",
					icon: "/favicon.ico",
				});
			}
		}
		prevStreaming.current = isStreaming;
	}, [isStreaming, messages, playReceive, isVisible]);

	/* Request notification permission on first interaction */
	useEffect(() => {
		if ("Notification" in window && Notification.permission === "default") {
			const handler = () => {
				Notification.requestPermission();
				document.removeEventListener("click", handler);
			};
			document.addEventListener("click", handler);
			return () => document.removeEventListener("click", handler);
		}
	}, []);

	/* Connection lost toast after multiple reconnect failures */
	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent).detail as { attempts: number };
			toast(`Connection lost. Retrying… (attempt ${detail.attempts})`, "error");
		};
		window.addEventListener("agent-connection-lost", handler);
		return () => window.removeEventListener("agent-connection-lost", handler);
	}, [toast]);

	return (
		<div
			className={cn(
				"flex h-[100dvh] bg-background text-foreground overflow-hidden",
				dragOver && "ring-2 ring-primary ring-inset"
			)}
			style={{ fontSize: `${fontSize}px` }}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			<Sidebar
				open={showSidebar}
				onClose={() => setSidebarOpen(false)}
				onNewChat={handleNewChat}
				width={sidebarWidth}
				onWidthChange={setSidebarWidth}
				workspace={workspace}
				onFileSelect={(path) => {
					setEditorPath(path);
					setEditorOpen(true);
				}}
				selectedFilePath={editorOpen ? editorPath : undefined}
				conversations={conversations}
				onSelectConversation={handleSelectConversation}
			/>

			{/* Main content */}
			<div className="flex-1 flex flex-col min-w-0">
				{/* Connection status bar */}
				<ConnectionStatusBar
					isConnected={isConnected}
					isOnline={isOnline}
					onRetry={() => window.location.reload()}
				/>

				{/* Header */}
				<header
					className={cn(
						"flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border bg-background/80 backdrop-blur-sm shrink-0 transition-transform duration-200",
						headerHidden && isMobile && "-translate-y-full"
					)}
				>
					<div className="flex items-center gap-2 min-w-0">
						{!showSidebar && (
							<button
								type="button"
								onClick={() => setSidebarOpen(true)}
								className="p-2 -ml-2 rounded-lg hover:bg-secondary transition-colors shrink-0"
								title="Open sidebar"
								aria-label="Open sidebar"
							>
								<Menu className="w-5 h-5" />
							</button>
						)}
						<h1 className="text-sm sm:text-base font-semibold hidden sm:block truncate">
							Boring Agent
						</h1>
					</div>

					<div className="flex items-center gap-1 sm:gap-2 shrink-0">
						<ConnectionBadge connected={isConnected} />

						<button
							type="button"
							onClick={() => setPaletteOpen(true)}
							className="hidden sm:flex p-2 rounded-lg hover:bg-secondary transition-colors"
							title="Command palette (⌘ K)"
						>
							<Command className="w-4 h-4" />
						</button>

						{hasMessages && (
							<button
								type="button"
								onClick={() => {
									handleNewChat();
									hapticMedium();
								}}
								className="p-2 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
								title="Clear chat"
							>
								<Trash2 className="w-4 h-4" />
							</button>
						)}

						<button
							type="button"
							onClick={() => setSettingsOpen(true)}
							className="p-2 rounded-lg hover:bg-secondary transition-colors"
							title="Settings"
						>
							<Settings className="w-4 h-4" />
						</button>
					</div>
				</header>

				{/* Drag overlay */}
				<AnimatePresence>
					{dragOver && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="absolute inset-0 z-40 bg-primary/5 backdrop-blur-sm flex items-center justify-center pointer-events-none"
						>
							<div className="bg-card border border-primary/30 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
								<Paperclip className="w-6 h-6 text-primary" />
								<span className="text-sm font-medium">Drop file to upload</span>
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				{/* Messages area */}
				<main
					ref={(el) => {
						// Assign to both refs
						(mainRef as React.MutableRefObject<HTMLElement | null>).current = el;
						(pullRef as React.MutableRefObject<HTMLDivElement | null>).current =
							el as HTMLDivElement;
					}}
					className="flex-1 overflow-y-auto scrollbar-thin relative"
					aria-live="polite"
					aria-atomic="false"
				>
					{/* Pull to refresh indicator */}
					{isMobile && (
						<div
							className={cn(
								"sticky top-0 z-10 flex items-center justify-center transition-all duration-200",
								pulling ? "opacity-100 h-12" : "opacity-0 h-0"
							)}
							style={{ transform: `translateY(${(progress - 1) * 20}px)` }}
						>
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<RefreshCw className={cn("w-4 h-4", progress >= 1 && "animate-spin")} />
								<span>{progress >= 1 ? "Release to refresh" : "Pull to refresh"}</span>
							</div>
						</div>
					)}
					<AnimatePresence mode="popLayout">
						{!hasMessages && isLoading ? (
							<motion.div
								key="skeleton"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
							>
								<SkeletonList count={2} />
							</motion.div>
						) : !hasMessages && !isConnected ? (
							<motion.div
								key="loading"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
							>
								<LoadingScreen />
							</motion.div>
						) : !hasMessages ? (
							<motion.div
								key="welcome"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
							>
								<WelcomeScreen onSuggestion={handleSuggestion} />
							</motion.div>
						) : (
							<motion.div
								key="chat"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="py-4 space-y-1"
							>
								{groupedMessages.map(({ message, isGrouped }, index) => (
									<MessageBubble
										key={message.id}
										message={message}
										isStreaming={
											(isStreaming || isThinking) &&
											index === groupedMessages.length - 1 &&
											message.role === "assistant"
										}
										isGrouped={isGrouped}
										onRetry={
											index === groupedMessages.length - 1 && message.role === "assistant"
												? handleRetry
												: undefined
										}
									/>
								))}
								<div ref={messagesEndRef} />
							</motion.div>
						)}
					</AnimatePresence>
				</main>

				{/* Scroll to bottom */}
				<AnimatePresence>
					{showScrollButton && (
						<motion.button
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							type="button"
							onClick={() => scrollToBottom()}
							className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-3 py-2 rounded-full bg-card border border-border shadow-lg text-xs font-medium hover:bg-secondary transition-colors"
						>
							<ChevronDown className="w-3.5 h-3.5" />
							<span>New messages</span>
						</motion.button>
					)}
				</AnimatePresence>

				{/* Input area */}
				<div className="shrink-0 border-t border-border bg-background p-3 sm:p-4 pb-safe">
					<div className="max-w-3xl mx-auto">
						{/* Attachment previews */}
						{attachments.length > 0 && (
							<div className="flex gap-2 mb-2 overflow-x-auto pb-1">
								{attachments.map((att) => (
									<div key={att.id} className="relative shrink-0 group">
										<img
											src={att.src}
											alt={att.name}
											className="w-14 h-14 object-cover rounded-lg border border-border"
										/>
										<button
											type="button"
											onClick={() => removeAttachment(att.id)}
											className="absolute -top-1 -right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
										>
											<X className="w-2.5 h-2.5" />
										</button>
									</div>
								))}
							</div>
						)}
						<div
							className={cn(
								"flex items-end gap-2 bg-card border border-border rounded-2xl p-2 sm:p-3 transition-all",
								"focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20"
							)}
						>
							{/* File upload */}
							<button
								type="button"
								onClick={() => fileInputRef.current?.click()}
								className="shrink-0 p-2 rounded-xl hover:bg-secondary transition-colors text-muted-foreground touch-target"
								title="Upload file"
							>
								<ImagePlus className="w-4 h-4 sm:w-5 sm:h-5" />
							</button>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*,.pdf,.txt,.md,.json,.js,.ts,.tsx"
								onChange={handleFileUpload}
								className="hidden"
							/>

							<textarea
								ref={textareaRef}
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder={isListening ? "Listening…" : "Message Boring Agent…"}
								rows={1}
								enterKeyHint="send"
								className="flex-1 bg-transparent resize-none outline-none text-sm sm:text-base placeholder:text-muted-foreground min-h-[24px] max-h-[200px] py-1 px-1"
								disabled={isListening || isThinking}
							/>

							{/* Voice input */}
							{voiceSupported && (
								<button
									type="button"
									onClick={() => {
										toggleVoice();
										hapticLight();
									}}
									className={cn(
										"shrink-0 p-2 rounded-xl transition-colors touch-target",
										isListening
											? "bg-red-500/10 text-red-400 animate-pulse"
											: "hover:bg-secondary text-muted-foreground"
									)}
									title={isListening ? "Stop listening" : "Voice input"}
								>
									{isListening ? (
										<MicOff className="w-4 h-4 sm:w-5 sm:h-5" />
									) : (
										<Mic className="w-4 h-4 sm:w-5 sm:h-5" />
									)}
								</button>
							)}

							{isStreaming || isThinking ? (
								<button
									type="button"
									onClick={() => {
										stop();
										hapticLight();
									}}
									className="shrink-0 p-2.5 sm:p-3 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-all touch-target animate-pulse"
									title="Stop generating"
								>
									<Square className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
								</button>
							) : (
								<button
									type="button"
									onClick={() => {
										handleSend();
										hapticLight();
									}}
									disabled={!input.trim() || !isConnected}
									className={cn(
										"shrink-0 p-2.5 sm:p-3 rounded-xl transition-all touch-target",
										input.trim() && isConnected
											? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
											: "bg-secondary text-muted-foreground cursor-not-allowed"
									)}
									title="Send message"
								>
									<Send className="w-4 h-4 sm:w-5 sm:h-5" />
								</button>
							)}
						</div>
						<p className="text-[10px] sm:text-xs text-muted-foreground mt-2 text-center">
							Shift + Enter for new line · Enter to send · ⌘ K for commands
							{isMobile && " · Swipe edge for sidebar"}
						</p>
					</div>
				</div>
			</div>

			<CommandPalette
				open={paletteOpen}
				onClose={() => setPaletteOpen(false)}
				commands={paletteCommands}
			/>

			<SettingsPanel
				open={settingsOpen}
				onClose={() => setSettingsOpen(false)}
				theme={theme}
				onThemeChange={setTheme}
				soundEnabled={soundEnabled}
				onSoundChange={setSoundEnabled}
				fontSize={fontSize}
				onFontSizeChange={setFontSize}
			/>

			<FileEditorPanel
				open={editorOpen}
				path={editorPath}
				onSave={workspace.saveFile}
				onClose={() => setEditorOpen(false)}
			/>
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  App Root                                                          */
/* ------------------------------------------------------------------ */
export default function App() {
	return (
		<ErrorBoundary>
			<ToastProvider>
				<AppContent />
			</ToastProvider>
		</ErrorBoundary>
	);
}
